/**
 * Line items eligible for “Other SVGs”: product title contains "initials" or "geburtsdaten".
 */

import { OTHER_SVG_CATEGORY, OTHER_SVG_CATEGORY_LABEL, OTHER_SVG_FIELDS_BY_CATEGORY, OTHER_SVG_LINE_KEYS_BY_CATEGORY } from './constants'

export function getLineItemDisplayTitle(lineItem) {
  if (!lineItem) return ''
  const t = lineItem.title ?? lineItem.name
  return t != null ? String(t).trim() : ''
}

/**
 * @returns {typeof OTHER_SVG_CATEGORY[keyof typeof OTHER_SVG_CATEGORY] | null}
 */
export function getOtherSvgCategory(lineItem) {
  const title = getLineItemDisplayTitle(lineItem).toLowerCase()
  if (title.includes('geburtsdaten')) return OTHER_SVG_CATEGORY.BIRTH_DATA
  if (title.includes('initials')) return OTHER_SVG_CATEGORY.INITIALS
  return null
}

export function getOtherSvgCategoryLabel(category) {
  return OTHER_SVG_CATEGORY_LABEL[category] || category
}

/**
 * @returns {{ lineItem: object, category: string, categoryLabel: string, title: string }[]}
 */
export function getOtherSvgLineEntries(order) {
  const items = order?.lineItems || []
  const out = []
  for (const li of items) {
    const category = getOtherSvgCategory(li)
    if (!category) continue
    out.push({
      lineItem: li,
      category,
      categoryLabel: getOtherSvgCategoryLabel(category),
      title: getLineItemDisplayTitle(li) || 'Product',
    })
  }
  return out
}

/** @param {string} category */
export function getOtherSvgFieldsForCategory(category) {
  return OTHER_SVG_FIELDS_BY_CATEGORY[category] || []
}

/** @param {{ key: string }[]} fields */
export function createEmptyFieldValues(fields) {
  const out = {}
  for (const { key } of fields) out[key] = ''
  return out
}

function propertyValueForKeyVariants(lineItem, keyVariants) {
  const props = lineItem?.properties || []
  const lowers = new Set(keyVariants.map((k) => String(k).trim().toLowerCase()))
  for (const { name, value } of props) {
    if (name == null) continue
    const n = String(name).trim()
    if (lowers.has(n.toLowerCase())) {
      return value != null ? String(value).trim() : ''
    }
  }
  return ''
}

/**
 * Prefill birth-data fields keyed by stable field keys (geburtsdatum, geburtszeit, …).
 * @returns {Record<string, string>}
 */
export function getBirthDataFieldValues(lineItem) {
  const fields = getOtherSvgFieldsForCategory(OTHER_SVG_CATEGORY.BIRTH_DATA)
  const out = createEmptyFieldValues(fields)
  for (const field of fields) {
    const keys = field.propertyKeys || [field.label]
    out[field.key] = propertyValueForKeyVariants(lineItem, keys)
  }
  return out
}

/** @deprecated use getBirthDataFieldValues */
export function getGeburtsdatenFieldValues(lineItem) {
  return getBirthDataFieldValues(lineItem)
}

/**
 * Prefill initials fields from properties whose name includes "initial" (case-insensitive).
 * Order follows the line item properties array (typically numbered 1–4 from Shopify).
 * @returns {Record<string, string>}
 */
export function getInitialsFieldValues(lineItem) {
  const fields = getOtherSvgFieldsForCategory(OTHER_SVG_CATEGORY.INITIALS)
  const out = createEmptyFieldValues(fields)
  const props = lineItem?.properties || []
  let i = 0
  for (const { name, value } of props) {
    if (name == null) continue
    if (!String(name).toLowerCase().includes('initial')) continue
    if (i >= fields.length) break
    out[fields[i].key] = value != null ? String(value).trim() : ''
    i += 1
  }
  return out
}

/**
 * @param {string} category — OTHER_SVG_CATEGORY.BIRTH_DATA | OTHER_SVG_CATEGORY.INITIALS
 * @returns {Record<string, string>}
 */
export function getOtherSvgPrefillFields(lineItem, category) {
  if (category === OTHER_SVG_CATEGORY.BIRTH_DATA) return getBirthDataFieldValues(lineItem)
  if (category === OTHER_SVG_CATEGORY.INITIALS) return getInitialsFieldValues(lineItem)
  return createEmptyFieldValues(getOtherSvgFieldsForCategory(category))
}

/** @param {string} category */
export function getOtherSvgLineKeysForCategory(category) {
  return OTHER_SVG_LINE_KEYS_BY_CATEGORY[category] || []
}

/**
 * Non-empty trimmed strings in API line order (for `data.lines`).
 * @param {Record<string, string>} fieldValues
 * @param {string[]} lineKeys
 */
export function buildOtherSvgLines(fieldValues, lineKeys) {
  const vals = fieldValues && typeof fieldValues === 'object' ? fieldValues : {}
  const keys = Array.isArray(lineKeys) ? lineKeys : []
  const out = []
  for (const key of keys) {
    const t = vals[key]
    if (t == null) continue
    const s = String(t).trim()
    if (s !== '') out.push(s)
  }
  return out
}
